import Vue from 'vue';
import VueRouter from 'vue-router';
import HomeView from '../views/HomeView.vue';
import ContactView from '../views/ContactView.vue';
import BeliefsView from '../views/BeliefsView.vue';
import BeginningsView from '../views/BeginningsView.vue';
import CapabilitiesView from '../views/CapabilitiesView.vue';
import CapabilityDetailView from '../views/CapabilityDetailView.vue';
import BenefitsView from '../views/BenefitsView.vue';
import PastAdventuresView from '../views/PastAdventuresView.vue';
import ReckonWayLandingView from '../views/ReckonWayLandingView.vue';
import AdventureGalleryView from '../views/AdventureGalleryView.vue';
import AdventureStoryView from '../views/AdventureStoryView.vue';
import TraditionsLandingView from '../views/TraditionsLandingView.vue';
import TraditionDetailView from '../views/TraditionDetailView.vue';
import CultureExperiencesView from '../views/CultureExperiencesView.vue';
import CultureExperienceRouteView from '../views/CultureExperienceRouteView.vue';
import CultureEatsView from '../views/CultureEatsView.vue';
import CulturePlacesView from '../views/CulturePlacesView.vue';
import CulturePlaceDetailView from '../views/CulturePlaceDetailView.vue';
import CulturePeopleView from '../views/CulturePeopleView.vue';
import CultureStoryView from '../views/CultureStoryView.vue';
import CultureStoriesView from '../views/CultureStoriesView.vue';
import ReckonBallArchiveView from '../views/ReckonBallArchiveView.vue';
import ReckonBallYearView from '../views/ReckonBallYearView.vue';
import PassportView from '../views/PassportView.vue';
import PassportStatView from '../views/PassportStatView.vue';
import PassportRecordView from '../views/PassportRecordView.vue';
import EventEntityView from '../views/EventEntityView.vue';
import HeroProfileView from '../views/HeroProfileView.vue';
import PowersView from '../views/PowersView.vue';
import LoginView from '../views/LoginView.vue';
import VeraKnowledgeAdminView from '../views/VeraKnowledgeAdminView.vue';
import { traditionsBySlug } from '../content/culture/traditions';
import { experiencesBySlug } from '../content/culture/experiences';
import { cultureStoriesBySlug } from '../content/culture/stories';
import { reckonBallByYear } from '../content/reckon-ball';
import { capabilitiesBySlug } from '../content/capabilities';

const DEFAULT_TITLE = 'wereckon.com';
const DEFAULT_DESCRIPTION = 'Reckon builds memorable experiences through technology, teamwork, and shared adventure.';
const CULTURE_SECTION_ROUTE_NAMES = new Set([
  'reckon-way',
  'reckon-way-the-way',
  'reckon-way-stories',
  'reckon-way-traditions',
  'reckon-way-experiences',
  'reckon-way-eats',
  'reckon-way-places',
  'reckon-way-people',
]);
let cultureSectionScrollPosition = null;

function isCultureSectionTransition(to, from) {
  return CULTURE_SECTION_ROUTE_NAMES.has(to.name)
    && CULTURE_SECTION_ROUTE_NAMES.has(from.name);
}

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
  },
  // route level code-splitting
  // this generates a separate chunk (about.[hash].js) for this route
  // which is lazy-loaded when the route is visited.
  {
    path: '/contact',
    name: 'contact',
    component: ContactView,
    meta: {
      title: 'Contact Reckon',
    },
  },
  {
    path: '/beliefs',
    name: 'beliefs',
    component: BeliefsView,
    meta: {
      title: 'What We Believe | Reckon',
      description: 'Learn what Reckon believes, why we exist, and the principles behind how we build exceptional teams and software.',
    },
  },
  {
    path: '/beginnings',
    name: 'beginnings',
    component: BeginningsView,
    meta: {
      title: 'Our Story | Reckon',
      description: 'Read the origin story of Reckon and the journey that shaped the company.',
    },
  },
  {
    path: '/about-us',
    name: 'about-us',
    redirect: { name: 'beliefs' },
  },
  {
    path: '/powers',
    name: 'powers',
    component: PowersView,
    meta: {
      title: 'Reckon Super Powers',
    },
  },
  {
    path: '/powers/:heroSlug',
    name: 'super-power-profile',
    component: HeroProfileView,
    meta: {
      title: 'Reckon Super Powers',
      comments: true,
    },
  },
  {
    path: '/heroes/:heroSlug',
    name: 'hero-profile',
    component: HeroProfileView,
    meta: {
      title: 'Reckon Hero Origin',
      comments: true,
    },
  },
  {
    path: '/passports/:employeeSlug',
    name: 'passport',
    component: PassportView,
    meta: {
      title: 'Reckon Passport',
    },
  },
  {
    path: '/passports/:employeeSlug/:statKey/:recordId',
    name: 'passport-record',
    component: PassportRecordView,
    meta: {
      title: 'Passport Record | Reckon Passport',
      comments: true,
    },
  },
  {
    path: '/passports/:employeeSlug/:statKey',
    name: 'passport-stat',
    component: PassportStatView,
    meta: {
      title: 'Passport Stat | Reckon Passport',
    },
  },
  {
    path: '/capabilities',
    name: 'capabilities',
    component: CapabilitiesView,
    meta: {
      title: 'Engineering Capabilities',
      description: 'Explore the engineering disciplines and delivery capabilities Reckon brings to mission-critical work.',
    },
  },
  {
    path: '/benefits',
    name: 'benefits',
    component: BenefitsView,
    meta: {
      title: 'Why Join Reckon?',
      description: 'Discover careers, growth, benefits, and life at Reckon.',
    },
  },
  {
    path: '/capabilities/agile-delivery',
    redirect: {
      name: 'capability-detail',
      params: { capabilitySlug: 'program-delivery' },
    },
  },
  {
    path: '/capabilities/:capabilitySlug',
    name: 'capability-detail',
    component: CapabilityDetailView,
    meta: {
      title: 'Reckon Engineering Capabilities',
      description: 'Explore Reckon engineering disciplines and delivery capabilities for mission-critical work.',
    },
  },
  {
    path: '/the-reckon-way',
    name: 'reckon-way',
    component: PastAdventuresView,
    meta: {
      title: 'The Reckon Way',
    },
  },
  {
    path: '/the-reckon-way/the-way',
    name: 'reckon-way-the-way',
    component: ReckonWayLandingView,
    meta: {
      title: 'The Way | Reckon',
    },
  },
  {
    path: '/the-reckon-way/stories',
    name: 'reckon-way-stories',
    component: CultureStoriesView,
    meta: {
      title: 'Stories Along the Way | Reckon',
      description: 'Read the published narratives behind Reckon journeys, traditions, and shared experiences.',
    },
  },
  {
    path: '/the-reckon-way/stories/:storySlug',
    name: 'reckon-way-culture-story',
    component: CultureStoryView,
    meta: {
      title: 'The Reckon Way Story',
      description: 'Stories about the journeys, traditions, and shared experiences that shape Reckon.',
      comments: true,
    },
  },
  {
    path: '/the-reckon-way/aws-reinvent-las-vegas',
    name: 'aws-reinvent',
    component: AdventureGalleryView,
    meta: {
      title: 'AWS re:Invent | The Reckon Way',
      description: 'Follow Reckon’s annual AWS re:Invent tradition through team adventures, Las Vegas restaurants, Sphere concerts, and the Reckon Crew.',
      eventSlug: 'aws-reinvent-las-vegas',
    },
  },
  {
    path: '/the-reckon-way/conferences/aws-reinvent',
    name: 'aws-reinvent-conference',
    component: AdventureGalleryView,
    meta: {
      title: 'AWS re:Invent | The Reckon Way',
      description: 'Follow Reckon’s annual AWS re:Invent tradition through team adventures, Las Vegas restaurants, Sphere concerts, and the Reckon Crew.',
      eventSlug: 'aws-reinvent-las-vegas',
    },
  },
  {
    path: '/the-reckon-way/:gallerySlug/stories/:storySlug',
    name: 'reckon-way-story',
    component: AdventureStoryView,
    meta: {
      title: 'Adventure Story',
      comments: true,
    },
  },
  {
    path: '/the-reckon-way/:gallerySlug/:sectionKey/:entityId/:storyPageSlug',
    name: 'reckon-way-entity-story-page',
    component: EventEntityView,
    meta: {
      title: 'Experience Story | The Reckon Way',
      description: 'Continue the story of a Reckon dining or team experience.',
      comments: true,
    },
  },
  {
    path: '/the-reckon-way/:gallerySlug/:sectionKey/:entityId',
    name: 'reckon-way-entity',
    component: EventEntityView,
    meta: {
      title: 'Experience | The Reckon Way',
      description: 'Explore the story and gallery for a Reckon dining or team experience.',
      comments: true,
    },
  },
  {
    path: '/the-reckon-way/jeep-off-road-tour-in-valley-of-fire-2024',
    redirect: {
      name: 'reckon-way-entity',
      params: {
        gallerySlug: 'aws-reinvent-las-vegas',
        sectionKey: 'activities',
        entityId: 'jeep-off-road-tour-in-valley-of-fire-2024',
      },
    },
  },
  {
    path: '/jeep-off-road-tour-in-valley-of-fire-2024',
    redirect: {
      name: 'reckon-way-entity',
      params: {
        gallerySlug: 'aws-reinvent-las-vegas',
        sectionKey: 'activities',
        entityId: 'jeep-off-road-tour-in-valley-of-fire-2024',
      },
    },
  },
  {
    path: '/the-reckon-way/traditions',
    name: 'reckon-way-traditions',
    component: TraditionsLandingView,
    meta: {
      title: 'Traditions | The Reckon Way',
      description: 'The small rituals and annual celebrations that make Reckon feel like Reckon.',
    },
  },
  {
    path: '/the-reckon-way/reckon-ball',
    name: 'reckon-ball-archive',
    component: ReckonBallArchiveView,
    meta: {
      title: 'Reckon Ball | The Reckon Way',
      description: 'Explore the annual Reckon Ball tradition from 2022 through 2025.',
    },
  },
  {
    path: '/the-reckon-way/reckon-ball/:year',
    name: 'reckon-ball-year',
    component: ReckonBallYearView,
    meta: {
      title: 'Reckon Ball | The Reckon Way',
      description: 'A year inside the annual Reckon Ball tradition.',
      comments: true,
    },
  },
  {
    path: '/the-reckon-way/traditions/reckon-ball',
    redirect: { name: 'reckon-ball-archive' },
  },
  {
    path: '/the-reckon-way/traditions/:traditionSlug',
    name: 'reckon-way-tradition',
    component: TraditionDetailView,
    meta: {
      title: 'Reckon Tradition | The Reckon Way',
      description: 'Explore a living Reckon tradition and the milestones that keep it growing.',
      comments: true,
    },
  },
  {
    path: '/the-reckon-way/experiences',
    name: 'reckon-way-experiences',
    component: CultureExperiencesView,
    meta: {
      title: 'Other Fun | The Reckon Way',
      description: 'Local outings, dinners, celebrations, and activities shared by the Reckon Crew.',
    },
  },
  {
    path: '/the-reckon-way/experiences/movie-night-alamo',
    redirect: { name: 'reckon-way-tradition', params: { traditionSlug: 'movie-night-alamo' } },
  },
  {
    path: '/the-reckon-way/experiences/:tagSlug',
    name: 'reckon-way-experience',
    component: CultureExperienceRouteView,
    meta: {
      title: 'Reckon Way Experience',
      comments: true,
    },
  },
  {
    path: '/the-reckon-way/places',
    name: 'reckon-way-places',
    component: CulturePlacesView,
    meta: {
      title: 'Places | The Reckon Way',
      description: 'The restaurants, breweries, venues, and gathering spots connected to Reckon traditions and experiences.',
    },
  },
  {
    path: '/the-reckon-way/eats',
    name: 'reckon-way-eats',
    component: CultureEatsView,
    meta: {
      title: 'Eats Along the Way | Reckon',
      description: 'Explore the restaurants, bars, breweries, and memorable meals that became part of the Reckon story.',
    },
  },
  {
    path: '/the-reckon-way/people',
    name: 'reckon-way-people',
    component: CulturePeopleView,
    meta: {
      title: 'Meet the Heroes | Reckon',
      description: 'Meet the Reckon Crew, discover their superhero identities, and explore the passports that collect their shared journeys and achievements.',
    },
  },
  {
    path: '/the-reckon-way/places/fogo-de-chao',
    redirect: { name: 'reckon-way-tradition', params: { traditionSlug: 'end-of-year-lunch' } },
  },
  {
    path: '/the-reckon-way/places/fogo-de-ch-o',
    redirect: { name: 'reckon-way-tradition', params: { traditionSlug: 'end-of-year-lunch' } },
  },
  {
    path: '/the-reckon-way/places/axes-and-o-s',
    redirect: { name: 'reckon-way-experience', params: { tagSlug: 'axes-and-os' } },
  },
  {
    path: '/the-reckon-way/places/k1-speed',
    redirect: { name: 'reckon-way-experience', params: { tagSlug: 'k1-speed' } },
  },
  {
    path: '/the-reckon-way/places/:placeSlug',
    name: 'reckon-way-place',
    component: CulturePlaceDetailView,
    meta: {
      title: 'Reckon Place | The Reckon Way',
      comments: true,
    },
  },
  {
    path: '/the-reckon-way/local-events',
    redirect: { name: 'reckon-way-experiences' },
  },
  {
    path: '/reckon-way/traditions',
    redirect: { name: 'reckon-way-traditions' },
  },
  {
    path: '/reckon-way/traditions/:traditionSlug',
    redirect: (to) => ({ name: 'reckon-way-tradition', params: to.params }),
  },
  {
    path: '/reckon-way/experiences',
    redirect: { name: 'reckon-way-experiences' },
  },
  {
    path: '/reckon-way/experiences/movie-night-alamo',
    redirect: { name: 'reckon-way-tradition', params: { traditionSlug: 'movie-night-alamo' } },
  },
  {
    path: '/reckon-way/experiences/:tagSlug',
    redirect: (to) => ({ name: 'reckon-way-experience', params: to.params }),
  },
  {
    path: '/reckon-way/eats',
    redirect: { name: 'reckon-way-eats' },
  },
  {
    path: '/reckon-way/people',
    redirect: { name: 'reckon-way-people' },
  },
  {
    path: '/reckon-way/local-events',
    redirect: { name: 'reckon-way-experiences' },
  },
  {
    path: '/the-reckon-way/:gallerySlug',
    name: 'reckon-way-gallery',
    component: AdventureGalleryView,
    meta: {
      title: 'Adventure Gallery',
    },
  },
  {
    path: '/past-adventures',
    redirect: { name: 'reckon-way' },
  },
  {
    path: '/past-adventures/:gallerySlug/stories/:storySlug',
    redirect: (to) => ({
      name: 'reckon-way-story',
      params: to.params,
    }),
  },
  {
    path: '/past-adventures/:gallerySlug',
    redirect: (to) => ({
      name: 'reckon-way-gallery',
      params: to.params,
    }),
  },
  {
    path: '/admin/vera-knowledge',
    name: 'vera-knowledge-admin',
    component: VeraKnowledgeAdminView,
    meta: {
      title: 'Vera Knowledge Administration | Reckon',
      description: 'Manage approved knowledge for the Reckon website assistant.',
    },
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: {
      title: 'Sign in | Reckon',
      description: 'Sign in to Reckon securely with your Google account.',
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Check if there is a saved position (e.g., using browser back/forward)
    if (savedPosition) {
      return savedPosition;
    }

    if (isCultureSectionTransition(to, from)) {
      return false;
    }

    if (to.hash) {
      if (to.hash.startsWith('#restaurant-')) {
        return new Promise((resolve) => {
          Vue.nextTick(() => {
            const target = document.getElementById(to.hash.slice(1));
            if (!target) {
              resolve({ selector: to.hash, offset: { x: 0, y: 72 } });
              return;
            }

            const fontsReady = document.fonts?.ready || Promise.resolve();
            const layoutReady = Promise.race([
              fontsReady,
              new Promise((finish) => {
                window.setTimeout(finish, 500);
              }),
            ]);
            layoutReady.then(() => {
              window.requestAnimationFrame(() => {
                const centeredTop = window.pageYOffset
                  + target.getBoundingClientRect().top
                  - ((window.innerHeight - target.offsetHeight) / 2);
                resolve({ x: 0, y: Math.max(0, centeredTop) });
              });
            });
          });
        });
      }

      return {
        selector: to.hash,
        offset: { x: 0, y: 72 },
      };
    }

    // Scroll to the top of the page
    return { x: 0, y: 0 };
  },
});

router.beforeEach((to, from, next) => {
  cultureSectionScrollPosition = isCultureSectionTransition(to, from)
    ? { x: window.pageXOffset, y: window.pageYOffset }
    : null;
  next();
});

router.afterEach((to, from) => {
  if (!isCultureSectionTransition(to, from) || !cultureSectionScrollPosition) return;
  const preservedPosition = cultureSectionScrollPosition;
  cultureSectionScrollPosition = null;

  Vue.nextTick(() => {
    window.requestAnimationFrame(() => {
      window.scrollTo(preservedPosition.x, preservedPosition.y);
      window.requestAnimationFrame(() => {
        window.scrollTo(preservedPosition.x, preservedPosition.y);
      });
    });
  });
});

// Not sure if this is where this belongs..
router.afterEach((to) => {
  // Use next tick to handle router history correctly
  // see: https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609
  Vue.nextTick(() => {
    const tradition = traditionsBySlug[to.params.traditionSlug];
    const experience = experiencesBySlug[to.params.tagSlug];
    const cultureStory = cultureStoriesBySlug[to.params.storySlug];
    const reckonBall = reckonBallByYear[to.params.year];
    const capability = capabilitiesBySlug[to.params.capabilitySlug];
    document.title = (capability && `${capability.title} | Reckon Engineering Capabilities`)
      || (reckonBall && `Reckon Ball ${reckonBall.year}: ${reckonBall.theme} | The Reckon Way`)
      || (tradition && (tradition.seoTitle || `${tradition.name} | The Reckon Way`))
      || (experience && (experience.seoTitle || `${experience.name} | The Reckon Way`))
      || (cultureStory && `${cultureStory.title} | The Reckon Way`)
      || to.meta.title
      || DEFAULT_TITLE;
    let description = document.querySelector('meta[name="description"]');

    if (!description) {
      description = document.createElement('meta');
      description.setAttribute('name', 'description');
      document.head.appendChild(description);
    }

    description.setAttribute(
      'content',
      (capability && capability.description)
        || (reckonBall && reckonBall.hero.summary)
        || (tradition && (tradition.seoDescription || tradition.summary))
        || (experience && (experience.seoDescription || experience.summary))
        || (cultureStory && cultureStory.excerpt)
        || to.meta.description
        || DEFAULT_DESCRIPTION,
    );
  });
});

export default router;
